import {
  Button,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { ReactNode } from "react";
import { fontFamily } from "html2canvas/dist/types/css/property-descriptors/font-family";

type Props = {
  children: ReactNode;
  message?: string | ReactNode;
  type?: "basic" | "medium" | "large";
  label?: string;
  customHeight?: string;
  fontSize?: string;
  getTooltipContent?: (value: any, index: number) => string;
  tooltipData?: any[];
  // Render the anchor wrapper inline (inline-flex, middle-aligned) so the
  // tooltip can sit inside running text — an icon after a name in a
  // comma-separated list. The default block wrapper would break the line.
  inline?: boolean;
};

const CustomTooltip = ({
  children,
  message,
  type = "basic",
  label,
  customHeight = "16px",
  fontSize,
  getTooltipContent,
  tooltipData,
  inline = false,
}: Props) => {
  const theme = useTheme();

  const CustomWidthTooltip = styled(
    ({
      className,
      type: _type,
      customFontSize: _customFontSize,
      ...props
    }: TooltipProps & {
      type?: "basic" | "medium" | "large";
      customFontSize?: string;
    }) => (
      <Tooltip
        arrow
        {...props}
        classes={{ popper: className }}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [-2, -4],
                },
              },
              // Keep a gap from the viewport edge: a tooltip anchored near the
              // right edge (the info icons in the edit side panel) otherwise
              // sits flush against the window.
              {
                name: "preventOverflow",
                options: {
                  padding: 16,
                },
              },
            ],
          },
        }}
        children={props.children}
        sx={{}}
      />
    )
  )(
    ({
      type,
      customFontSize,
    }: {
      type?: "basic" | "medium" | "large";
      customFontSize?: string;
    }) => ({
      [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: type === "large" ? 550 : 252,
        borderRadius: "8px",
        padding: type === "medium" ? "8px 12px" : "8px 8px 8px 16px",
        boxShadow: "var(--box-shadow-general)",
        backgroundColor: theme.colorTokens.P[10],
        color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
        fontSize:
          customFontSize || (type === "large" ? "10px !important" : "12px"),
        fontFamily: "var(--font-Medium)",
        lineHeight: "16px",
      },
      [`& .${tooltipClasses.arrow}::before`]: {
        backgroundColor: theme.colorTokens.P[10],
      },
    })
  );

  const renderTooltipContent = () => {
    if (getTooltipContent && tooltipData && tooltipData.length > 0) {
      return (
        <div style={styles.tooltipContentContainer}>
          <div style={styles.tooltipContentLabel(theme)}>{label}</div>
          {tooltipData.map((data, index) => {
            const content = getTooltipContent(data, index);
            if (!content) return null;

            return (
              <div key={index}>
                {index === 0 || index % 2 === 0 ? (
                  <div style={styles.headerItem(theme)}>{content}</div>
                ) : (
                  <div style={styles.insightItem(theme)}>{content}</div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  return (
    <CustomWidthTooltip
      title={
        typeof message === "string" ? (
          message
        ) : (
          <div>
            {getTooltipContent && tooltipData ? (
              renderTooltipContent()
            ) : (
              <>
                {label && (
                  <div
                    style={
                      type === "medium"
                        ? styles.mediumLabel(theme)
                        : styles.label(theme)
                    }
                  >
                    {label}
                  </div>
                )}
                <div
                  style={
                    type === "medium"
                      ? styles.mediumContainer(theme)
                      : styles.container(theme)
                  }
                  dangerouslySetInnerHTML={{
                    __html: (message as any)?.props?.children,
                  }}
                />
              </>
            )}
          </div>
        )
      }
      type={type}
      customFontSize={fontSize}
    >
      <div
        style={
          inline
            ? {
                height: customHeight,
                display: "inline-flex",
                alignItems: "center",
                verticalAlign: "middle",
              }
            : { height: customHeight }
        }
      >
        {children}
      </div>
    </CustomWidthTooltip>
  );
};

export default CustomTooltip;

const styles = {
  container: (theme: any) => ({
    maxHeight: 280,
    margin: "8px -8px",
    padding: "8px 16px 8px 8px",
    overflowY: "scroll" as "scroll",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    // Custom scrollbar styles
    "&::WebkitScrollbar": {
      width: "2px", // Width of the scrollbar
    },
    "&::WebkitScrollbarThumb": {
      width: "2px",
      borderRadius: "10px", // Rounded scrollbar thumb
    },
    "&::WebkitScrollbarTrack": {
      background: "transparent", // Scrollbar track color
    },
    scrollbarColor: `${theme.colors_ds.NEUTRAL_BORDER_DEFAULT} transparent`, // For Firefox
    scrollbarWidth: "thin" as "thin", // For Firefox
  }),
  label: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    marginTop: "8px",
  }),
  mediumLabel: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    marginBottom: "4px",
  }),
  mediumContainer: (theme: any) => ({
    maxHeight: 280,
    overflowY: "auto" as "auto",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    scrollbarColor: `${theme.colors_ds.NEUTRAL_BORDER_DEFAULT} transparent`,
    scrollbarWidth: "thin" as "thin",
  }),
  tooltipContentContainer: {
    maxHeight: 280,
    margin: "8px -8px",
    padding: "8px 16px 8px 8px",
    overflowY: "auto" as "auto",
  },
  tooltipContentLabel: (theme: any) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
  }),
  tooltipItem: {
    marginBottom: "8px",
  },
  headerItem: (theme: any) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    marginTop: "8px",
  }),
  insightItem: (theme: any) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    lineHeight: "18px",
  }),
};
